/**
 * Villagers: a stack of boxes in the villager's colour with a floating name
 * label, a bundle on their back when they are carrying something, a progress
 * ring while a multi-tick task runs, and a "zzz" while asleep.
 *
 * Server positions arrive once per tick on whole voxels (at most one step), so
 * each view lerps from the previous cell to the new one over `tickMs`, with a
 * walking bob and an eased heading (`facing` is atan2(dz, dx): 0 = east).
 */
import * as THREE from 'three';
import { carried, type Resource, type Villager } from '@village/shared';
import { mixHex } from '../palette';
import type { Terrain } from './terrain';
import { makeLabel } from './label';

const TAU = Math.PI * 2;

const CARRY_COLOR: Record<Resource, string> = {
  wood: '#D3AF7F',
  stone: '#BDB9C9',
  dirt: '#C4A183',
  food: '#C7DFA6',
};

function shortestAngle(from: number, to: number): number {
  let d = (to - from) % TAU;
  if (d > Math.PI) d -= TAU;
  if (d < -Math.PI) d += TAU;
  return d;
}

/** The model faces +z, `facing` measures from +x toward +z. */
const yawOf = (facing: number) => Math.PI / 2 - facing;

function part(w: number, h: number, d: number, color: string): THREE.Mesh {
  const mesh = new THREE.Mesh(
    new THREE.BoxGeometry(w, h, d),
    new THREE.MeshStandardMaterial({ color: new THREE.Color(color), roughness: 0.82, metalness: 0, flatShading: true }),
  );
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

function dominantResource(v: Villager): Resource | null {
  let best: Resource | null = null;
  let most = 0;
  for (const key of ['wood', 'stone', 'dirt', 'food'] as Resource[]) {
    if (v.inventory[key] > most) {
      most = v.inventory[key];
      best = key;
    }
  }
  return most > 0 ? best : null;
}

class VillagerView {
  root = new THREE.Group();
  private body = new THREE.Group();
  private legL: THREE.Mesh;
  private legR: THREE.Mesh;
  private bundle: THREE.Mesh;
  private ring: THREE.Mesh;
  private ringTrack: THREE.Mesh;
  private zzz: THREE.Sprite;
  private select: THREE.Mesh;

  private from = new THREE.Vector2();
  private to = new THREE.Vector2();
  private fromY = 0;
  private toY = 0;
  private facingFrom = 0;
  private facingTo = 0;
  private moving = false;
  private asleep = false;
  private ringProgress = -1;

  constructor(v: Villager, terrain: Terrain) {
    const color = v.color;
    const legColor = mixHex(color, '#3B3A48', 0.35);
    const headColor = mixHex(color, '#FFF1E2', 0.72);

    this.legL = part(0.11, 0.2, 0.12, legColor);
    this.legL.position.set(-0.08, 0.1, 0);
    this.legR = part(0.11, 0.2, 0.12, legColor);
    this.legR.position.set(0.08, 0.1, 0);

    const torso = part(0.32, 0.34, 0.24, color);
    torso.position.y = 0.37;
    const armL = part(0.07, 0.26, 0.1, mixHex(color, '#FFFFFF', 0.18));
    armL.position.set(-0.2, 0.38, 0);
    const armR = armL.clone();
    armR.position.x = 0.2;
    const head = part(0.26, 0.24, 0.24, headColor);
    head.position.y = 0.67;
    const hat = part(0.3, 0.07, 0.3, mixHex(color, '#3B3A48', 0.18));
    hat.position.y = 0.81;
    const nose = part(0.05, 0.05, 0.06, mixHex(headColor, '#3B3A48', 0.12));
    nose.position.set(0, 0.66, 0.13);

    this.bundle = part(0.2, 0.17, 0.12, CARRY_COLOR.wood);
    this.bundle.position.set(0, 0.42, -0.18);
    this.bundle.visible = false;

    this.body.add(this.legL, this.legR, torso, armL, armR, head, hat, nose, this.bundle);
    this.root.add(this.body);

    const label = makeLabel(v.name, { height: 0.3 });
    label.position.y = 1.12;
    this.root.add(label);

    this.zzz = makeLabel('z z z', { height: 0.22, bg: 'rgba(227,215,255,0.92)' });
    this.zzz.position.set(0.22, 0.86, 0);
    this.zzz.visible = false;
    this.root.add(this.zzz);

    const ringMat = new THREE.MeshBasicMaterial({ color: new THREE.Color(color), transparent: true, opacity: 0.95, side: THREE.DoubleSide, depthWrite: false });
    this.ring = new THREE.Mesh(new THREE.RingGeometry(0.15, 0.21, 20, 1, Math.PI / 2, 0), ringMat);
    this.ring.rotation.x = -Math.PI / 2;
    this.ring.position.y = 0.96;
    this.ring.visible = false;
    this.ringTrack = new THREE.Mesh(
      new THREE.RingGeometry(0.15, 0.21, 24),
      new THREE.MeshBasicMaterial({ color: new THREE.Color('#FFFFFF'), transparent: true, opacity: 0.5, side: THREE.DoubleSide, depthWrite: false }),
    );
    this.ringTrack.rotation.x = -Math.PI / 2;
    this.ringTrack.position.y = 0.955;
    this.ringTrack.visible = false;
    this.root.add(this.ringTrack, this.ring);

    this.select = new THREE.Mesh(
      new THREE.TorusGeometry(0.38, 0.04, 6, 24),
      new THREE.MeshStandardMaterial({
        color: new THREE.Color('#FFF2B3'),
        emissive: new THREE.Color('#FFD98A'),
        emissiveIntensity: 0.8,
        roughness: 0.5,
        transparent: true,
        opacity: 0.95,
      }),
    );
    this.select.rotation.x = -Math.PI / 2;
    this.select.position.y = 0.03;
    this.select.visible = false;
    this.root.add(this.select);

    this.root.userData = { villagerId: v.id, pickable: true };
    this.body.traverse((o) => {
      o.userData.villagerId = v.id;
    });

    this.from.set(v.x, v.z);
    this.to.set(v.x, v.z);
    this.fromY = this.toY = terrain.surfaceY(v.x, v.z);
    this.facingFrom = this.facingTo = yawOf(v.facing);
    this.root.position.set(v.x, this.toY, v.z);
    this.body.rotation.y = this.facingTo;
    this.apply(v);
  }

  target(v: Villager, terrain: Terrain): void {
    this.from.copy(this.to);
    this.fromY = this.toY;
    this.to.set(v.x, v.z);
    this.toY = terrain.surfaceY(v.x, v.z);
    this.facingFrom = this.body.rotation.y;
    this.facingTo = yawOf(v.facing);
    this.moving = this.from.distanceToSquared(this.to) > 1e-4;
    this.apply(v);
  }

  /** State that is not interpolated: carrying, sleeping, task progress. */
  private apply(v: Villager): void {
    const res = dominantResource(v);
    this.bundle.visible = carried(v.inventory) > 0;
    if (res) (this.bundle.material as THREE.MeshStandardMaterial).color.set(CARRY_COLOR[res]);

    this.asleep = v.asleep;
    this.zzz.visible = v.asleep;

    const task = v.task;
    const p = task && task.total > 0 ? Math.max(0, Math.min(1, task.progress / task.total)) : 0;
    const show = !!task && !v.asleep;
    this.ring.visible = show;
    this.ringTrack.visible = show;
    if (show) {
      const q = Math.round(p * 24) / 24;
      if (q !== this.ringProgress) {
        this.ringProgress = q;
        this.ring.geometry.dispose();
        this.ring.geometry = new THREE.RingGeometry(0.15, 0.21, 20, 1, Math.PI / 2, q * TAU);
      }
    }
  }

  setSelected(on: boolean): void {
    this.select.visible = on;
  }

  update(t: number, time: number): void {
    const x = this.from.x + (this.to.x - this.from.x) * t;
    const z = this.from.y + (this.to.y - this.from.y) * t;
    const y = this.fromY + (this.toY - this.fromY) * t;
    const bob = this.asleep ? 0 : this.moving ? Math.abs(Math.sin(time * 9 + this.from.x)) * 0.035 : Math.sin(time * 1.6) * 0.008;
    this.root.position.set(x, y + bob, z);

    const turn = shortestAngle(this.facingFrom, this.facingTo);
    this.body.rotation.y = this.facingFrom + turn * Math.min(1, t * 1.6);
    // asleep: tipped over on the ground, and the legs stop swinging
    this.body.rotation.z = THREE.MathUtils.lerp(this.body.rotation.z, this.asleep ? Math.PI / 2.2 : 0, 0.12);
    this.body.position.y = THREE.MathUtils.lerp(this.body.position.y, this.asleep ? 0.12 : 0, 0.12);

    const swing = this.moving && !this.asleep ? Math.sin(time * 9 + this.from.x) * 0.5 : 0;
    this.legL.rotation.x = swing * 0.5;
    this.legR.rotation.x = -swing * 0.5;

    if (this.select.visible) {
      const s = 1 + Math.sin(time * 3) * 0.06;
      this.select.scale.set(s, s, 1);
    }
    if (this.ring.visible) this.ring.rotation.z = -time * 0.6;
  }

  dispose(): void {
    this.root.traverse((o) => {
      const m = o as THREE.Mesh;
      m.geometry?.dispose();
      const mat = m.material as THREE.Material | THREE.Material[] | undefined;
      if (Array.isArray(mat)) mat.forEach((x) => x.dispose());
      else mat?.dispose();
    });
  }
}

export class VillagerLayer {
  readonly group = new THREE.Group();
  private views = new Map<string, VillagerView>();
  private tickAt = performance.now();
  private selected: string | null = null;

  constructor(private terrain: Terrain, private tickMs: number) {
    this.group.name = 'villagers';
  }

  sync(villagers: Villager[]): void {
    this.tickAt = performance.now();
    const seen = new Set<string>();
    for (const v of villagers) {
      seen.add(v.id);
      const view = this.views.get(v.id);
      if (!view) {
        const fresh = new VillagerView(v, this.terrain);
        fresh.setSelected(this.selected === v.id);
        this.views.set(v.id, fresh);
        this.group.add(fresh.root);
      } else {
        view.target(v, this.terrain);
      }
    }
    for (const [id, view] of this.views) {
      if (seen.has(id)) continue;
      this.group.remove(view.root);
      view.dispose();
      this.views.delete(id);
    }
  }

  setSelected(id: string | null): void {
    this.selected = id;
    for (const [vid, view] of this.views) view.setSelected(vid === id);
  }

  update(now: number): void {
    const t = Math.max(0, Math.min(1, (now - this.tickAt) / this.tickMs));
    const time = now / 1000;
    for (const [, view] of this.views) view.update(t, time);
  }
}
